import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Lock, Gavel, FileCheck2, HeartPulse, Home, Pill } from 'lucide-react';
import RequestAccessForm from './RequestAccessForm';
import { CATALOG_ENGINE_COUNT } from '@/lib/engineCount';

const METRICS = [
  { k: 'Avoidable ER visits/yr', v: '$59B' },
  { k: 'Annual healthcare fraud', v: '$100B' },
  { k: 'ARR per 2.5M-life contract', v: '$5–12.5M' },
  { k: 'Audit trail', v: 'FRE 902(13)' },
];

function Metric({ k, v }) {
  return (
    <div className="bg-white border border-amber-200 rounded-xl p-4" data-testid={`payer-metric-${k.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="text-2xl font-bold tracking-tight" style={{ color: '#B45309', fontFamily: "'Work Sans',sans-serif" }}>{v}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">{k}</div>
    </div>
  );
}

function Acc({ value, icon: Icon, title, children }) {
  return (
    <AccordionItem value={value} className="bg-white border border-amber-200 rounded-lg mb-3 overflow-hidden">
      <AccordionTrigger data-testid={`payer-acc-${value}`} className="px-5 py-4 hover:bg-amber-50 hover:no-underline text-base font-semibold text-slate-800">
        <span className="flex items-center gap-2 text-left">
          <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
          {title}
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-5 pb-5 pt-1 text-sm text-slate-600 border-t border-amber-100 leading-relaxed">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

export default function PayerSurface() {
  return (
    <section className="physician-output max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="payer-surface">
      <div className="border-2 border-amber-400 bg-amber-50 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="payer-paywall">
        <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <p className="text-sm font-semibold text-amber-900">Health Mart paywall · Enterprise licensing · $2–5 PMPM.</p>
      </div>

      <RequestAccessForm surface="payer" />

      <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Work Sans',sans-serif" }}>Payer AI</h2>
      <div className="grid grid-cols-2 gap-3 mb-8">
        {METRICS.map((m) => <Metric key={m.k} {...m} />)}
      </div>

      <Accordion type="multiple" className="w-full">
        <Acc value="fraud" icon={Gavel} title="Fraud signal detection (Moat H) · hash-chained · court-admissible">
          <p>Deterministic billing code vs clinical documentation match. Flags upcoding, unbundling, phantom billing. FRE 902(13) self-authenticating. Not an LLM guess — a rule fired or it did not. Admissible in federal court without expert testimony.</p>
        </Acc>

        <Acc value="priorauth" icon={FileCheck2} title="Prior authorization automation (Moat C) · $13B annual burden">
          <p>Auto-populates PA from SOAP note. Deterministic payer criteria match. 2h staff time → seconds. $11 per PA request eliminated. Reduces denials and appeals for both payer and provider.</p>
        </Acc>

        <Acc value="eravoid" icon={HeartPulse} title="ER avoidance · population health · preventive flags">
          <p>{CATALOG_ENGINE_COUNT} preventive engines flag high-risk patients before ER visit occurs. USPSTF Grade A/B delivered at point of care. Patient-owned FHIR data source — no hospital permission required. No competitor has this.</p>
        </Acc>

        <Acc value="dme" icon={Home} title="DME + home health CMS compliance (Moat D+E)">
          <p>Auto-generates Certificate of Medical Necessity from SOAP note. OASIS-E mapping for Medicare home health billing. 65% of DME claims denied first submission. $700 recovery per corrected claim. $15–25 per CMN generated.</p>
        </Acc>

        <Acc value="dea" icon={Pill} title="Telehealth DEA compliance (Moat G) · Ryan Haight Act">
          <p>Deterministic compliance checklist for controlled substance prescribing via telehealth. Documents patient verification, state licensing, DEA registration match. DEA violation fine avoided: $10K–$100K per infraction. 300,000+ telehealth-prescribing physicians in US.</p>
        </Acc>
      </Accordion>
    </section>
  );
}
